import { INodeMapping, IVersionMappingUpdate } from '../tree/TreeActions'
import * as React from 'react'
import { Box, Button, TextField } from '@mui/material'
import { ToggleButton, ToggleButtonGroup } from '@mui/lab'
import { goToNode } from '../masters_management/utility'
import { mappingReviewUrl } from 'infrastructure/api'
import {
  filteredMappings,
  findBestMatch,
  INodeIdInfo,
  MappingFilterType,
  NodeIdMap,
} from './utility'
import { INode } from 'shared/types/tree'
import { ReactElement } from 'react'

export function MatchedMappings({
  matched,
  sourceIdMapping,
  destIdMapping,
  destTree,
  sourceTree,
  updateMappingFunc,
  reviewFilters = [MappingFilterType.UserGenerated],
  reviewIndex = 0,
}: {
  matched: INodeMapping[]
  sourceIdMapping: NodeIdMap
  destIdMapping: NodeIdMap
  destTree: INode
  sourceTree: INode
  updateMappingFunc: (updateInfo: IVersionMappingUpdate) => void
  reviewFilters?: MappingFilterType[]
  reviewIndex?: number
}) {
  const [currentIndex, setCurrentIndex] = React.useState(-2)
  const [currentMapping, setCurrentMapping] = React.useState<INodeMapping>()
  const [currentSet, setCurrentSet] = React.useState<INodeMapping[]>([])
  const [selectedFilters, setSelectedFilters] =
    React.useState<MappingFilterType[]>(reviewFilters)
  const [mappingTitle, setMappingTitle] = React.useState('')

  React.useEffect(() => {
    const filtered = filteredMappings(
      matched,
      sourceIdMapping,
      destIdMapping,
      ...selectedFilters,
    )
    setCurrentSet(filtered)
    if (currentIndex === -2 && reviewIndex < filtered.length) {
      setCurrentIndex(reviewIndex)
    } else {
      setCurrentIndex(0)
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [matched, selectedFilters, sourceIdMapping, destIdMapping, reviewIndex])

  React.useEffect(() => {
    setCurrentMapping(currentSet[currentIndex])
  }, [currentIndex, currentSet])

  const next = () => {
    if (currentIndex < currentSet.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      setCurrentIndex(0)
    }
  }
  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    } else {
      setCurrentIndex(currentSet.length - 1)
    }
  }

  function mappingUrl() {
    if (!currentMapping) return
    const fromTree = sourceIdMapping[currentMapping.from_node_id].node
    const toTree = destIdMapping[currentMapping.to_node_id!].node
    if (!fromTree || !toTree) return
    const url = mappingReviewUrl({
      fromTree,
      toTree,
      filters: selectedFilters,
      index: currentIndex,
    })
    navigator.clipboard.writeText(url)
  }

  function goToTitle() {
    if (!mappingTitle) return
    currentSet.slice(currentIndex + 1).some((mapping, index) => {
      const node = sourceIdMapping[mapping.from_node_id].node
      const toNode = destIdMapping[mapping.to_node_id!].node
      if (
        node?.title?.toLowerCase().includes(mappingTitle) ||
        toNode?.title?.toLowerCase().includes(mappingTitle)
      ) {
        setCurrentIndex(index + currentIndex + 1)
        return true
      }
      return false
    })
  }

  return (
    <Box padding={'10px'}>
      <Box>
        <ToggleButtonGroup
          size={'small'}
          value={selectedFilters}
          onChange={(e, v) => setSelectedFilters(v)}
        >
          <ToggleButton value={MappingFilterType.UserGenerated}>
            User Generated
          </ToggleButton>
          <ToggleButton value={MappingFilterType.MismatchedPaths}>
            Mismatched Paths
          </ToggleButton>
          <ToggleButton value={MappingFilterType.CriticalMismatchedPaths}>
            Critical Mismatched Paths
          </ToggleButton>
          <ToggleButton value={MappingFilterType.MismatchedTitles}>
            Mismatched Titles
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Box padding={'5px'}>
        {currentIndex + 1} of {currentSet.length}
      </Box>
      <Box>
        <TextField
          onChange={({ target: { value } }) =>
            setMappingTitle(value.toLowerCase())
          }
        />
        <Button onClick={goToTitle}>Find Title</Button>
      </Box>
      <Box padding={'10px'}>
        {currentMapping && (
          <Box>
            <Box>
              MappingID: {currentMapping.id}{' '}
              <Button onClick={mappingUrl}>*</Button>
            </Box>
            <MatchedMapping
              mapping={currentMapping}
              allMappings={matched}
              updateMappingFunc={updateMappingFunc}
              fromNodeData={sourceIdMapping[currentMapping.from_node_id]}
              toNodeData={destIdMapping[currentMapping.to_node_id!]}
              destIdMapping={destIdMapping}
              destTree={destTree}
              sourceTree={sourceTree}
              sourceIdMapping={sourceIdMapping}
            />
          </Box>
        )}
        <Box border={'1px solid'} width={'135px'}>
          <Button onClick={prev}>{'<-'}</Button>
          <Button onClick={next}>{'->'}</Button>
        </Box>
      </Box>
    </Box>
  )
}

function MatchedMapping({
  mapping,
  allMappings,
  updateMappingFunc,
  fromNodeData,
  toNodeData,
  destTree,
  sourceTree,
  destIdMapping,
  sourceIdMapping,
}: {
  mapping: INodeMapping
  allMappings: INodeMapping[]
  updateMappingFunc: (updateInfo: IVersionMappingUpdate) => void
  fromNodeData: INodeIdInfo
  toNodeData?: INodeIdInfo
  destTree: INode
  sourceTree: INode
  destIdMapping: NodeIdMap
  sourceIdMapping: NodeIdMap
}) {
  const [betterMatch, setBetterMatch] = React.useState<INode | undefined>()
  const [betterUsed, setBetterUsed] = React.useState<INodeMapping | undefined>()

  React.useEffect(() => {
    setBetterMatch(undefined)
    setBetterUsed(undefined)
    if (toNodeData?.treePath === fromNodeData.treePath) {
      return
    }
    const match = findBestMatch({
      path: fromNodeData.treePath,
      destTree,
      sourceTree,
    })
    if (!toNodeData || match?.id !== toNodeData.node.id) {
      setBetterMatch(match)
      if (match) {
        const used = allMappings.find((m) => m.to_node_id === match.id)
        if (used) {
          setBetterUsed(used)
        }
      }
    }
  }, [toNodeData, fromNodeData, destTree])

  function doReset() {
    updateMappingFunc({
      id: mapping.id,
      deleted: false,
      to_node_id: null,
    })
  }

  interface IMatchData {
    title: string
    from?: ReactElement
    to?: ReactElement
  }
  function currentMappingDisplays(): IMatchData[] {
    let deletedData: IMatchData | null = null
    if (mapping.deleted) {
      deletedData = {
        title: 'Deleted',
      }
    }
    let betterMatchData: IMatchData | null = null
    if (betterMatch && !mapping.deleted) {
      betterMatchData = {
        title: 'Better Match',
        from: (
          <Button onClick={() => goToNode(betterMatch)}>
            {betterMatch.title}
          </Button>
        ),
        to: (
          <Box>
            <Box>{destIdMapping[betterMatch.id].treePath}</Box>
            <Box>{betterMatch.body?.ExternalID}</Box>
            <Box>{betterMatch.id}</Box>
            {betterUsed && (
              <Box>
                <b>Already Used:</b>{' '}
                {sourceIdMapping[betterUsed.from_node_id].treePath}
              </Box>
            )}
          </Box>
        ),
      }
    }
    return [
      {
        title: 'Paths',
        from: fromNodeData.treePath,
        to: toNodeData?.treePath,
      },
      deletedData,
      betterMatchData,
      {
        title: 'Types',
        from: fromNodeData.node.type,
        to: toNodeData?.node.type,
      },
      {
        title: 'ExternalIDs',
        from: fromNodeData.node.body?.ExternalID,
        to: toNodeData?.node.body?.ExternalID,
      },
    ].filter((d) => !!d) as IMatchData[]
  }

  return (
    <Box>
      <Box>
        <Button onClick={doReset}>Reset Mapping</Button>
      </Box>
      <Box padding={'5px'}>
        <Box>
          <b>Titles</b>
        </Box>
        <Box>
          <Button onClick={() => goToNode(fromNodeData.node)}>
            {fromNodeData.node.title}
          </Button>
        </Box>
        {toNodeData && (
          <Box>
            <Button onClick={() => goToNode(toNodeData.node)}>
              {toNodeData.node.title}
            </Button>
          </Box>
        )}
      </Box>
      {currentMappingDisplays().map(({ title, from, to }) => {
        return (
          <Box key={title} padding={'5px'}>
            <Box>
              <b>{title}</b>
            </Box>
            <Box>{from}</Box>
            <Box>{to}</Box>
          </Box>
        )
      })}
    </Box>
  )
}
